import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid,
} from '@mui/material';
import { salvarCustasDadosJuridicos } from '../../../../services/core/processos/processoDadosJuridicosService';
import { toBrazilianDate } from '../../../../helpers/utils/utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível salvar a custa. Tente novamente.';

function dataHojeIso() {
  return new Date().toISOString().split('T')[0];
}

function makeFormFieldsIniciais() {
  return { data: dataHojeIso(), historico: '', valor: '' };
}

/**
 * Port de `DialogNovoCustas.jsx` real (`DadosJuridicos/TabelaCustas/`) para
 * MUI v5 — AW2-1108 Etapa 3. Campo "Data" usa `TextField type="date"` (mesmo
 * padrão já estabelecido em `DialogTitulo.jsx`, sem `DateField`/máscara real)
 * — convertido para o formato brasileiro (`toBrazilianDate`) só no momento do
 * submit, já que `salvarCustasDadosJuridicos` aplica `toAmericanDate`
 * internamente (espera `DD/MM/YYYY`, não ISO).
 *
 * Sem Snackbar próprio: sucesso e erro fecham o dialog IMEDIATAMENTE (mesmo
 * comportamento real — o `catch` também fecha, não deixa aberto para nova
 * tentativa), então qualquer Snackbar renderizado aqui desapareceria junto
 * (regra já documentada em `src/prototipo/CLAUDE.md`, "Feedback de sucesso
 * que fecha o dialog deve viver no componente pai"). `onSucesso`/`onErro` são
 * chamados pelo componente pai (`AbaJuridico.jsx`), que possui o Snackbar.
 */
export default function DialogNovoCustas({
  open, idProcesso, onClose, onSucesso, onErro,
}) {
  const [formFields, setFormFields] = useState(makeFormFieldsIniciais);

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setFormFields(makeFormFieldsIniciais());
  });

  const onChangeCampo = nome => (event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, [nome]: value }));
  };

  // Mesma validação client-side da tela real (`disableConfirmarCustas`).
  const disableConfirmar = !formFields.data || !formFields.historico || !formFields.valor;

  const onSubmit = async () => {
    if (enviandoRef.current || disableConfirmar) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;

    try {
      await salvarCustasDadosJuridicos(
        toBrazilianDate(formFields.data), formFields.historico, Number(formFields.valor), idProcesso,
      );
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onClose();
      onSucesso();
    } catch {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onClose();
      onErro(MENSAGEM_ERRO_GENERICA);
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Inclusão no Valor de Custas do Processo</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              fullWidth
              label="Data*"
              value={formFields.data}
              onChange={onChangeCampo('data')}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Histórico*"
              value={formFields.historico}
              onChange={onChangeCampo('historico')}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              type="number"
              fullWidth
              label="Valor*"
              value={formFields.valor}
              onChange={onChangeCampo('valor')}
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={onSubmit} variant="contained" disabled={disableConfirmar}>Confirmar</Button>
      </DialogActions>
    </Dialog>
  );
}

DialogNovoCustas.propTypes = {
  open: PropTypes.bool,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func.isRequired,
  onErro: PropTypes.func.isRequired,
};

DialogNovoCustas.defaultProps = {
  open: false,
  idProcesso: null,
};
